<div class="vm-status-container">
  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-state">
    <nz-spin [nzSize]="'large'"></nz-spin>
    <p class="loading-text">Loading VM status...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !isLoading" class="error-state">
    <nz-result
      [nzStatus]="'error'"
      [nzTitle]="'Failed to Load VM Status'"
      [nzSubTitle]="error">
      <div nz-result-extra>
        <button nz-button nzType="primary" (click)="loadVmStatus()">
          <span nz-icon nzType="reload"></span>
          Retry
        </button>
      </div>
    </nz-result>
  </div>

  <!-- VM Status Content -->
  <div *ngIf="vmStatus && !isLoading" class="vm-status-content">
    <!-- Status Header -->
    <div class="status-header">
      <div class="status-badge-section">
        <nz-badge
          [nzStatus]="getStatusBadge()?.nzStatus"
          [nzText]="getStatusBadge()?.text">
        </nz-badge>
        <span class="last-updated" *ngIf="lastUpdated">
          Last updated: {{ getRelativeTime(lastUpdated) }}
        </span>
      </div>
      <div class="polling-indicator" *ngIf="isPolling">
        <nz-spin [nzSize]="'small'"></nz-spin>
        <span class="polling-text">Live monitoring</span>
      </div>
    </div>

    <!-- Status Message -->
    <div class="status-message">
      <p>{{ getStatusMessage() }}</p>
      <div class="estimated-time" *ngIf="vmStatus.vmCreationStatus === 'InProgress' && vmStatus.estimatedCompletionTime">
        <span nz-icon nzType="clock-circle" nzTheme="outline"></span>
        {{ getEstimatedTimeDisplay() }}
      </div>
    </div>

    <!-- Progress Section (for InProgress status) -->
    <div *ngIf="vmStatus.vmCreationStatus === 'InProgress'" class="progress-section">
      <nz-progress
        [nzPercent]="vmStatus.progressPercentage || 0"
        [nzStatus]="'active'"
        [nzShowInfo]="true">
      </nz-progress>
    </div>

    <!-- VM Details Section -->
    <div *ngIf="shouldShowVmDetails()" class="vm-details">
      <nz-divider [nzText]="'VM Details'" [nzOrientation]="'left'"></nz-divider>

      <div class="details-grid">
        <div class="detail-item" *ngIf="vmStatus.vmName">
          <span class="detail-label">VM Name:</span>
          <span class="detail-value">{{ vmStatus.vmName }}</span>
        </div>
        <div class="detail-item" *ngIf="vmStatus.vmSize">
          <span class="detail-label">VM Size:</span>
          <span class="detail-value">{{ vmStatus.vmSize }}</span>
        </div>
        <div class="detail-item" *ngIf="vmStatus.publicIpAddress">
          <span class="detail-label">Public IP:</span>
          <span class="detail-value">{{ vmStatus.publicIpAddress }}</span>
        </div>
        <div class="detail-item" *ngIf="vmStatus.privateIpAddress">
          <span class="detail-label">Private IP:</span>
          <span class="detail-value">{{ vmStatus.privateIpAddress }}</span>
        </div>
        <div class="detail-item" *ngIf="vmStatus.adminUsername">
          <span class="detail-label">Admin Username:</span>
          <span class="detail-value">{{ vmStatus.adminUsername }}</span>
        </div>
      </div>
    </div>

    <!-- Request Information -->
    <div class="request-info">
      <nz-collapse>
        <nz-collapse-panel
          [nzHeader]="'Request Information'"
          [nzActive]="false"
          [nzShowArrow]="true">
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Requester:</span>
              <span class="info-value">{{ vmStatus.requesterName || vmStatus.requesterEmail }}</span>
            </div>
            <div class="info-item" *ngIf="vmStatus.serviceDescription">
              <span class="info-label">Service Description:</span>
              <span class="info-value">{{ vmStatus.serviceDescription }}</span>
            </div>
            <div class="info-item" *ngIf="vmStatus.gpuRequirement">
              <span class="info-label">GPU Required:</span>
              <span class="info-value">
                <nz-tag [nzColor]="vmStatus.gpuRequirement ? 'orange' : 'default'">
                  {{ vmStatus.gpuRequirement ? 'Yes' : 'No' }}
                </nz-tag>
              </span>
            </div>
            <div class="info-item" *ngIf="vmStatus.softwareSet">
              <span class="info-label">Software Set:</span>
              <span class="info-value">{{ vmStatus.softwareSet }}</span>
            </div>
            <div class="info-item" *ngIf="vmStatus.justification">
              <span class="info-label">Justification:</span>
              <span class="info-value">{{ vmStatus.justification }}</span>
            </div>
          </div>
        </nz-collapse-panel>
      </nz-collapse>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons" *ngIf="canRetry()">
      <button
        nz-button
        nzType="primary"
        nzDanger
        (click)="retryVmCreation()"
        [nzLoading]="isLoading">
        <span nz-icon nzType="redo"></span>
        Retry VM Creation
      </button>
    </div>

    <!-- Status History Toggle -->
    <div class="history-toggle">
      <button
        nz-button
        nzType="default"
        nzGhost
        (click)="toggleHistory()">
        <span nz-icon [nzType]="showHistory ? 'eye-invisible' : 'eye'"></span>
        {{ showHistory ? 'Hide' : 'Show' }} Status History
      </button>
    </div>

    <!-- Status History -->
    <div *ngIf="showHistory" class="status-history">
      <nz-divider [nzText]="'Status History'" [nzOrientation]="'left'"></nz-divider>

      <nz-timeline>
        <nz-timeline-item
          *ngFor="let entry of history; let i = index"
          [nzColor]="getTimelineColor(entry.newStatus)">
          <div class="history-entry">
            <div class="history-header">
              <strong>{{ entry.statusMessage }}</strong>
              <span class="history-time">{{ getRelativeTime(entry.timestamp) }}</span>
            </div>
            <div class="history-details" *ngIf="entry.technicalDetails">
              <p>{{ entry.technicalDetails }}</p>
            </div>
            <div class="history-meta">
              <span class="history-updated-by">Updated by: {{ entry.updatedBy }}</span>
              <nz-tag *ngIf="entry.isAutomated" [nzColor]="'blue'">Automated</nz-tag>
            </div>
          </div>
        </nz-timeline-item>

        <nz-timeline-item *ngIf="history.length === 0">
          <div class="no-history">
            <p>No status history available</p>
          </div>
        </nz-timeline-item>
      </nz-timeline>
    </div>
  </div>

  <!-- Empty State (no VM status available) -->
  <div *ngIf="!vmStatus && !isLoading && !error" class="empty-state">
    <nz-empty
      [nzNotFoundImage]="'https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPRvNY/empty.svg'"
      [nzNotFoundContent]="'No VM status information available'">
    </nz-empty>
  </div>
</div>